import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { css } from '@emotion/react';
import { zodResolver } from '@hookform/resolvers/zod';
import CloseIcon from '@mui/icons-material/Close';
import { Box, ButtonBase, CircularProgress, IconButton, Modal } from '@mui/material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';

import { createQRCodeFolder, updateQRCodeFolder } from '~/api/qrCodeFolders';
import { Button } from '~/components/ui/button';
import { Text } from '~/components/ui/text';
import TextInput from '~/components/ui/TextInput';
import { folderKeys, qrCodeKeys } from '~/constants/queryKeys';
import { COLORS } from '~/styles/colors';

type CreateEditFolderModalProps = {
  isOpen: boolean;
  onClose: () => void;
  onSuccessCreate?: (folder: number) => void;
  defaultFolderName?: string;
  folderId?: number;
  type: 'create' | 'edit';
};

export default function CreateEditFolderModal({
  isOpen,
  onClose,
  onSuccessCreate,
  folderId,
  defaultFolderName,
  type,
}: CreateEditFolderModalProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const styles = useStyles();

  const folderNameSchema = z.object({
    folderName: z
      .string({ message: t('inputs:required') })
      .min(1, { message: t('inputs:required') })
      .max(100, { message: t('errors:max', { count: 100 }) }),
  });

  type FolderNameSchema = z.infer<typeof folderNameSchema>;

  const { control, handleSubmit } = useForm<FolderNameSchema>({
    defaultValues: { folderName: defaultFolderName ?? '' },
    resolver: zodResolver(folderNameSchema),
  });

  const { mutate: createQRCodeFolderMutate, isPending: isPendingCreateQRCodeFolder } = useMutation({
    mutationFn: createQRCodeFolder,
    onSuccess: async (response) => {
      await queryClient.invalidateQueries({ queryKey: folderKeys.allFolders });

      onClose();

      if (onSuccessCreate) {
        onSuccessCreate(response.data.id);
      }
    },
  });

  const { mutate: updateQRCodeFolderMutate, isPending: isPendingUpdateQRCodeFolder } = useMutation({
    mutationFn: updateQRCodeFolder,
    onSuccess: async (response) => {
      await queryClient.invalidateQueries({ queryKey: folderKeys.allFolders });

      onClose();

      if (response.data.qrCodesCount) {
        await queryClient.invalidateQueries({ queryKey: qrCodeKeys.myQrsData });
      }
    },
  });

  const handleCreateEditFolder = handleSubmit((values) => {
    if (type === 'create') {
      createQRCodeFolderMutate({ name: values.folderName });
    } else {
      if (values.folderName === defaultFolderName) {
        onClose();
      } else {
        updateQRCodeFolderMutate({ payload: { name: values.folderName }, folderId });
      }
    }
  });

  return (
    <Modal
      open={isOpen}
      sx={styles.modal}
      slotProps={{
        backdrop: {
          sx: styles.backdrop,
        },
      }}
    >
      <Box
        component="form"
        onSubmit={(e) => {
          e.preventDefault();
          return handleCreateEditFolder();
        }}
        sx={styles.container}
      >
        <IconButton onClick={onClose} sx={styles.closeIconWrapper}>
          <CloseIcon sx={styles.closeIcon} />
        </IconButton>

        <Text variant="h2" sx={styles.title}>
          {type === 'create' ? t('builder:nameAndFolder.createNewFolder') : t('folders:changeFolderName')}
        </Text>

        <Controller
          control={control}
          name="folderName"
          render={({ field: { value, onChange, onBlur }, fieldState: { error } }) => {
            return (
              <TextInput
                value={value}
                onChange={onChange}
                onBlur={onBlur}
                error={!!error?.message}
                helperText={error?.message}
                fullWidth
                inputProps={{ maxLength: 100 }}
                label={t('labels:name')}
                placeholder={t('common:enterName')}
              />
            );
          }}
        />

        <Box sx={styles.bottomActions}>
          <ButtonBase sx={styles.bottomBtn} onClick={onClose} disableRipple>
            {t('buttons:cancel')}
          </ButtonBase>
          <Button
            htmlType="submit"
            disableRipple
            endIcon={
              isPendingCreateQRCodeFolder || isPendingUpdateQRCodeFolder ? (
                <CircularProgress size={20} color="inherit" />
              ) : undefined
            }
            size="small"
            sx={styles.submitBtn}
          >
            {type === 'create' ? t('buttons:create') : t('buttons:save')}
          </Button>
        </Box>
      </Box>
    </Modal>
  );
}

function useStyles() {
  return {
    backdrop: css`
      background-color: #c9c8c84d;
      backdrop-filter: blur(3px);
    `,
    modal: css`
      display: flex;
      align-items: center;
      justify-content: center;
    `,
    closeIconWrapper: css`
      margin-left: auto;
    `,
    container: css`
      width: 100%;
      max-width: 475px;
      padding: 20px 32px 40px;
      border-radius: 20px;
      background-color: ${COLORS.white};
      display: flex;
      flex-direction: column;
    `,
    closeIcon: css`
      color: ${COLORS.textMain};
    `,
    title: css`
      cursor: initial;
      text-align: center;
      margin-bottom: 24px;
    `,
    bottomBtn: css`
      height: 40px;
      padding: 4px 20px;
      box-sizing: border-box;
      font-weight: 500;
      font-size: 18px;
      color: ${COLORS.textMain};
      width: fit-content;
    `,
    submitBtn: css`
      height: 40px;
      padding: 4px 20px !important;
    `,
    bottomActions: css`
      display: flex;
      align-items: center;
      justify-content: space-between;
      margin-top: 32px;
    `,
  };
}
